import React, { useState, useEffect, useRef } from "react";
import {
	Dialog,
	Box,
	InputBase,
	Typography,
	Divider,
	List,
	ListItemText,
	ListItemButton,
	ListItemIcon,
	CircularProgress,
	Grid,
	IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import CustomerIcon from "@mui/icons-material/PersonOutline";
import InvoiceIcon from "@mui/icons-material/Receipt";
import ContractIcon from "@mui/icons-material/Description";
import ProductIcon from "@mui/icons-material/LocalMall";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { SearchItem, SearchResult } from "@/types/search";

interface SearchDialogProps {
	open: boolean;
	onClose: () => void;
}

const QUICK_MENUS = [
	{
		title: "Харилцагч",
		items: [
			{
				label: "Шинэ харилцагч",
				url: "/customer/create",
				icon: <CustomerIcon fontSize="small" />,
			},
			{
				label: "Бүх харилцагч",
				url: "/customers",
				icon: <CustomerIcon fontSize="small" />,
			},
		],
	},
	{
		title: "Гэрээ",
		items: [
			{
				label: "Гэрээ үүсгэх",
				url: "/contracts/create",
				icon: <ContractIcon fontSize="small" />,
			},
			{
				label: "Бүх гэрээ",
				url: "/contracts",
				icon: <ContractIcon fontSize="small" />,
			},
		],
	},
	{
		title: "Нэхэмжлэх",
		items: [
			{
				label: "Бүх нэхэмжлэх",
				url: "/invoice",
				icon: <InvoiceIcon fontSize="small" />,
			},
		],
	},
	{
		title: "Бараа",
		items: [
			{
				label: "Бараа удирдах",
				url: "/products",
				icon: <ProductIcon fontSize="small" />,
			},
		],
	},
];

const HighlightedText = ({ text }: { text?: string }) => {
	if (!text) return null;
	return (
		<span
			dangerouslySetInnerHTML={{
				__html: text.replace(
					/<em>(.*?)<\/em>/gi,
					'<em style="color: #90caf9; font-style: normal; font-weight: bold; background-color: rgba(144, 202, 249, 0.16); padding: 0 4px; border-radius: 4px;">$1</em>',
				),
			}}
		/>
	);
};

const SearchItemRow = ({
	item,
	onNavigate,
}: {
	item: SearchResult;
	onNavigate: (url: string, item?: SearchResult) => void;
}) => {
	const theme = useTheme();
	const hasChildren =
		(item.contracts?.length ?? 0) > 0 || (item.recentInvoices?.length ?? 0) > 0;

	return (
		<Box sx={{ mb: hasChildren ? 2 : 0.5 }}>
			<ListItemButton
				onClick={() => onNavigate(item.url ?? "#", item)}
				sx={{
					borderRadius: 2,
					mb: hasChildren ? 0.5 : 0,
					bgcolor: "rgba(255, 255, 255, 0.05)",
					"&:hover": {
						bgcolor: "rgba(255, 255, 255, 0.15)",
					},
				}}
			>
				<ListItemIcon sx={{ minWidth: 40 }}>
					{item.type === "customer" && <CustomerIcon />}
					{item.type === "invoice" && <InvoiceIcon />}
					{item.type === "contract" && <ContractIcon />}
					{item.type === "product" && <ProductIcon />}
				</ListItemIcon>
				<ListItemText
					primary={<HighlightedText text={item.title} />}
					secondary={<HighlightedText text={item.subtitle} />}
				/>
				{item.rankingScore !== undefined && item.rankingScore !== null && (
					<Box
						sx={{
							ml: 2,
							flexShrink: 0,
							width: 36,
							height: 36,
							borderRadius: "50%",
							bgcolor: theme.palette.primary.main,
							boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.6)}`,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "0.8rem",
							fontWeight: "bold",
							color: theme.palette.primary.contrastText,
						}}
					>
						{Number(item.rankingScore).toFixed(1)}
					</Box>
				)}
			</ListItemButton>

			{hasChildren && (
				<Box sx={{ pl: 4, position: "relative" }}>
					{/* Vertical connecting line */}
					<Box
						sx={{
							position: "absolute",
							left: 19,
							top: 0,
							bottom: 16,
							width: "2px",
							bgcolor: "rgba(255, 255, 255, 0.1)",
						}}
					/>

					<List disablePadding>
						{item.contracts?.map((contract, idx) => (
							<Box
								key={`contract-${idx}`}
								sx={{
									position: "relative",
									display: "flex",
									alignItems: "center",
									mb: 0.5,
								}}
							>
								<Box
									sx={{
										position: "absolute",
										left: -21,
										top: "50%",
										width: "16px",
										height: "2px",
										bgcolor: "rgba(255, 255, 255, 0.1)",
									}}
								/>
								<ListItemButton
									onClick={() => onNavigate(`/contracts/view/${contract.id}`)}
									sx={{
										borderRadius: 2,
										py: 0.5,
										pl: 1,
										"&:hover": {
											bgcolor: "rgba(255, 255, 255, 0.1)",
										},
									}}
								>
									<ListItemIcon sx={{ minWidth: 32 }}>
										<ContractIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={<HighlightedText text={contract.label} />}
										secondary={
											<HighlightedText text={`Гэрээ · ${contract.systemId}`} />
										}
										primaryTypographyProps={{
											fontSize: "0.85rem",
											component: "div",
										}}
										secondaryTypographyProps={{
											fontSize: "0.75rem",
											component: "div",
										}}
									/>
									<Box sx={{ ml: 2 }}>
										<Typography
											variant="caption"
											sx={{
												px: 1,
												py: 0.5,
												bgcolor:
													contract.status === "active"
														? alpha(theme.palette.success.main, 0.2)
														: alpha(theme.palette.warning.main, 0.2),
												color:
													contract.status === "active"
														? theme.palette.success.main
														: theme.palette.warning.main,
												borderRadius: 1,
											}}
										>
											{contract.status}
										</Typography>
									</Box>
								</ListItemButton>
							</Box>
						))}
						{item.recentInvoices?.slice(0, 2).map((invoice, idx) => (
							<Box
								key={`inv-${idx}`}
								sx={{
									position: "relative",
									display: "flex",
									alignItems: "center",
									mb: 0.5,
								}}
							>
								<Box
									sx={{
										position: "absolute",
										left: -21,
										top: "50%",
										width: "16px",
										height: "2px",
										bgcolor: "rgba(255, 255, 255, 0.1)",
									}}
								/>
								<ListItemButton
									onClick={() => onNavigate(`/invoice/${invoice.uid}`)}
									sx={{
										borderRadius: 2,
										py: 0.5,
										pl: 1,
										"&:hover": {
											bgcolor: "rgba(255, 255, 255, 0.1)",
										},
									}}
								>
									<ListItemIcon sx={{ minWidth: 32 }}>
										<InvoiceIcon fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={
											<HighlightedText
												text={`₮${new Intl.NumberFormat().format(Number(String(invoice.total).replace(/<em>|<\/em>/gi, "")))}`}
											/>
										}
										secondary={
											<HighlightedText text={`Нэхэмжлэх · ${invoice.uid}`} />
										}
										primaryTypographyProps={{
											fontSize: "0.85rem",
											fontWeight: 600,
											component: "div",
										}}
										secondaryTypographyProps={{
											fontSize: "0.75rem",
											component: "div",
										}}
									/>
									<Box sx={{ ml: 2 }}>
										<Typography
											variant="caption"
											sx={{
												px: 1,
												py: 0.5,
												bgcolor:
													invoice.status === "paid"
														? alpha(theme.palette.success.main, 0.2)
														: alpha(theme.palette.error.main, 0.2),
												color:
													invoice.status === "paid"
														? theme.palette.success.main
														: theme.palette.error.main,
												borderRadius: 1,
											}}
										>
											{invoice.status}
										</Typography>
									</Box>
								</ListItemButton>
							</Box>
						))}
					</List>
				</Box>
			)}
		</Box>
	);
};

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onClose }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [items, setItems] = useState<SearchResult[]>([]);
	const [recentSearches, setRecentSearches] = useState<SearchItem[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (open) {
			setSearch("");
			setItems([]);
			// Load recent searches from localStorage if needed
			const storedRecents = localStorage.getItem("recentSearches");
			if (storedRecents) {
				try {
					setRecentSearches(JSON.parse(storedRecents));
				} catch (e) {}
			}
			// Focus input smoothly after opening
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [open]);

	useEffect(() => {
		if (search.length === 0) {
			setItems([]);
			return;
		}

		const timeout = setTimeout(() => {
			setIsLoading(true);
			fetch(`${process.env.REACT_APP_API_URL}/search/indexed?q=${search}`)
				.then((response) => response.json())
				.then((data) => {
					setItems(data.data || []);
				})
				.catch(() => {})
				.finally(() => {
					setIsLoading(false);
				});
		}, 300);

		return () => clearTimeout(timeout);
	}, [search]);

	const handleNavigate = (url: string, item?: SearchResult) => {
		if (item) {
			const stripTags = (txt: string) => txt?.replace(/<em>|<\/em>/gi, "") || "";
			const recentItem: SearchItem = {
				type: item.type,
				title: stripTags(item.title),
				subtitle: item.subtitle ? stripTags(item.subtitle) : undefined,
				url: item.url,
				image: undefined,
			};
			const updatedRecents = [
				recentItem,
				...recentSearches.filter((r) => r.url !== item.url),
			].slice(0, 5);
			setRecentSearches(updatedRecents);
			localStorage.setItem("recentSearches", JSON.stringify(updatedRecents));
		}
		navigate(url);
		onClose();
	};

	const handleRemoveRecent = (e: React.MouseEvent, url: string) => {
		e.stopPropagation();
		const updatedRecents = recentSearches.filter((r) => r.url !== url);
		setRecentSearches(updatedRecents);
		localStorage.setItem("recentSearches", JSON.stringify(updatedRecents));
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			PaperProps={{
				sx: {
					borderRadius: 3,
					bgcolor: "#121212",
					color: "#ffffff",
					backgroundImage: "none",
					boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
					border: `1px solid #333333`,
					overflow: "hidden",
					"& .MuiTypography-root, & .MuiInputBase-input, & .MuiListItemIcon-root":
						{
							color: "#ffffff",
						},
					"& .MuiTypography-colorTextSecondary": {
						color: "#aaaaaa",
					},
					"& .MuiDivider-root": {
						borderColor: "#333333",
					},
				},
			}}
			transitionDuration={0}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					p: 2,
					borderBottom: `1px solid #333333`,
				}}
			>
				{isLoading ? (
					<CircularProgress size={24} sx={{ mr: 2 }} color="inherit" />
				) : (
					<SearchIcon sx={{ mr: 2 }} color="inherit" />
				)}
				<InputBase
					inputRef={inputRef}
					placeholder="Та юу хайж байна вэ?"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ flex: 1, fontSize: "1.25rem" }}
					// Handle enter press if needed? Let's just rely on clicking items for now.
				/>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						bgcolor: "rgba(255, 255, 255, 0.1)",
						borderRadius: 1,
						px: 1,
						py: 0.5,
						ml: 2,
					}}
				>
					<Typography variant="caption" sx={{ fontWeight: "bold" }}>
						esc
					</Typography>
				</Box>
			</Box>

			<Box
				sx={{
					p: 2,
					minHeight: 300,
					maxHeight: "60vh",
					overflowY: "auto",
					scrollbarWidth: "thin",
					scrollbarColor: "rgba(255,255,255,0.2) transparent",
					"&::-webkit-scrollbar": {
						width: "6px",
					},
					"&::-webkit-scrollbar-track": {
						background: "transparent",
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(255,255,255,0.2)",
						borderRadius: "10px",
					},
					"&::-webkit-scrollbar-thumb:hover": {
						backgroundColor: "rgba(255,255,255,0.3)",
					},
				}}
			>
				{search.length > 0 ? (
					<Box>
						<List disablePadding>
							{items.length === 0 && !isLoading && (
								<Box sx={{ p: 4, textAlign: "center" }}>
									<Typography color="text.secondary">
										No results found for "{search}"
									</Typography>
								</Box>
							)}
							{items.map((item, index) => (
								<SearchItemRow
									key={index}
									item={item}
									onNavigate={handleNavigate}
								/>
							))}
						</List>
					</Box>
				) : (
					<Box>
						{recentSearches.length > 0 && (
							<Box sx={{ mb: 3 }}>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ fontWeight: "bold", mb: 1, display: "block" }}
								>
									Сүүлд хайсан
								</Typography>
								<List disablePadding>
									{recentSearches.map((recent, idx) => (
										<ListItemButton
											key={idx}
											onClick={() => handleNavigate(recent.url || "")}
											sx={{
												borderRadius: 2,
												mb: 0.5,
												bgcolor: "rgba(255, 255, 255, 0.05)",
												border: `1px solid rgba(255, 255, 255, 0.05)`,
												"&:hover": {
													bgcolor: "rgba(255, 255, 255, 0.1)",
												},
											}}
										>
											<ListItemIcon
												sx={{ minWidth: 40, color: theme.palette.primary.main }}
											>
												<HistoryIcon fontSize="small" />
											</ListItemIcon>
											<ListItemText primary={recent.title.replace(/<em>|<\/em>/gi, "")} />
											<IconButton
												size="small"
												color="inherit"
												onClick={(e) => handleRemoveRecent(e, recent.url || "")}
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</ListItemButton>
									))}
								</List>
							</Box>
						)}

						<Grid container spacing={3}>
							{QUICK_MENUS.map((menu, pIdx) => (
								<Grid item xs={12} sm={6} key={pIdx}>
									<Typography
										variant="overline"
										color="text.secondary"
										sx={{ fontWeight: "bold", mb: 1, display: "block", pl: 1 }}
									>
										{menu.title}
									</Typography>
									<List disablePadding>
										{menu.items.map((item, idx) => (
											<ListItemButton
												key={idx}
												onClick={() => handleNavigate(item.url)}
												sx={{
													borderRadius: 2,
													mb: 0.5,
													border: `1px solid rgba(255, 255, 255, 0.05)`,
													bgcolor: "rgba(0, 0, 0, 0.2)",
													"&:hover": {
														bgcolor: "rgba(255, 255, 255, 0.05)",
													},
												}}
											>
												<ListItemIcon sx={{ minWidth: 40 }}>
													{item.icon}
												</ListItemIcon>
												<ListItemText
													primary={item.label}
													primaryTypographyProps={{
														fontSize: "12px",
														fontWeight: 500,
														textTransform: "capitalize",
													}}
												/>
											</ListItemButton>
										))}
									</List>
								</Grid>
							))}
						</Grid>
					</Box>
				)}
			</Box>
			<Box
				sx={{
					p: 1.5,
					display: "flex",
					justifyContent: "end",
					borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
					bgcolor: "rgba(0, 0, 0, 0.1)",
				}}
			>
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
						fontSize: "0.7rem",
						opacity: 0.8,
					}}
					textAlign={"right"}
				>
					super search by saas.mn
				</Typography>
			</Box>
		</Dialog>
	);
};

export default SearchDialog;
